/**
 * This component is specifically used for rendering IEP analysis progress.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import makeStyles from '@mui/styles/makeStyles'

import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '../../icons/CircleCheckFilled'
import ErrorIcon from '@mui/icons-material/Error'

import { getPostById } from '../../api'
import { simplePlural } from '../../common'
import { PROGRESS_STATUS_FAILED } from '../../iep/constants'
import { useIepProgressPercent, usePlanTypeNavigationContextProvider } from '../../iep/hooks'
import model from '../../model'
import { trackPlanReviewStarted } from '../../tracking/segment'

import progressBackground from '../../assets/andy-ai-background.png'

import SummarySparkle from '../../icons/SummarySparkle'

import BrandButton from '../../subcomponents/brand/BrandButton'
import BrandIconButton from '../../subcomponents/brand/BrandIconButton'
import Emblem from '../../subcomponents/Emblem'
import PercentRing from '../../subcomponents/PercentRing'

const CHECK_DIMENSION = 1.75
const CLOSE_DIMENSION = 2.5

const useStyles = makeStyles(
  theme => ({
    root: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      backgroundImage: `url(${progressBackground})`,
      backgroundPosition: 'top left',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 100%',
      borderColor: theme.palette.border,
      borderRadius: theme.spacing(1),
      borderStyle: 'solid',
      borderWidth: theme.spacing(0.125),
      boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.05)',
      color: theme.palette.text.primary,
      columnAlign: 'center',
      columnGap: theme.spacing(2),
      display: 'flex',
      padding: theme.spacing(2)
    },

    stageContainer: {
      alignItems: 'center',
      columnGap: theme.spacing(1),
      display: 'flex',
      flexGrow: 1
    },

    stageEmblem: {
      borderRadius: theme.spacing(0.25),
      backgroundImage: [
        'linear-gradient(51deg',
        theme.palette.green.G400,
        '20.97%',
        `${theme.palette.green.border} 107.62%)`
      ].join(', ')
    },

    stageIcon: {
      height: theme.spacing(1.75),
      width: theme.spacing(1.75),

      '& path': {
        fill: theme.palette.common.white
      }
    },

    stageText: {
      ...theme.typography.body1,

      fontWeight: theme.typography.fontWeightMedium
    },

    statusContainer: {
      ...theme.typography.body2,

      fontWeight: theme.typography.fontWeightMedium
    },

    terminalStatusContainer: {
      alignItems: 'center',
      columnGap: theme.spacing(0.5),
      display: 'flex'
    },

    statusIcon: {
      borderRadius: theme.spacing(CHECK_DIMENSION),
      color: theme.palette.success.main,
      height: theme.spacing(CHECK_DIMENSION),
      minHeight: theme.spacing(CHECK_DIMENSION),
      minWidth: theme.spacing(CHECK_DIMENSION),
      width: theme.spacing(CHECK_DIMENSION)
    },

    readyIcon: {
      color: theme.palette.success.main
    },

    errorIcon: {
      color: theme.palette.error.dark
    },

    percentRing: {},
    reviewButton: {},

    closeButton: {
      color: theme.palette.text.primary,
      margin: theme.spacing(0, -0.5, 0, 0),
      padding: theme.spacing(0.25)
    },

    closeIcon: {
      height: theme.spacing(CLOSE_DIMENSION),
      width: theme.spacing(CLOSE_DIMENSION)
    }
  }),
  {
    name: 'IepProgress'
  }
)

const IepProgress = props => {
  const { className, progress, onClose } = props

  const {
    documentId,
    extraction_fully_complete: extractionCompleted,
    pipeline_status: phase,
    plan_type: planType,
    status,
    text
  } = progress ?? {}

  const { iepPath } = usePlanTypeNavigationContextProvider(simplePlural(planType))
  const { iepId: iepIdParam } = useParams()

  const [post, setPost] = useState()

  const currentPercent = useIepProgressPercent(phase, extractionCompleted)

  const { client } = post ?? {}

  const handleClose = useCallback(
    event => {
      if (onClose) {
        onClose(event)
      }
    },
    [onClose]
  )

  const handleTrackAndClose = useCallback(
    event => {
      trackPlanReviewStarted(planType, documentId, 'progressBar')
      handleClose(event)
    },
    [documentId, handleClose, planType]
  )

  useEffect(() => {
    if (!documentId) {
      setPost(null)
      return
    }

    let active = true

    const retrieveIepPost = async () => {
      try {
        const post = await getPostById(documentId)
        if (active) {
          setPost(model.post(post))
        }
      } catch (error) {
        // No-op for now.
      }
    }

    retrieveIepPost()
    return () => {
      active = false
    }
  }, [documentId])

  useEffect(() => {
    // If we move into a route that has our IEP ID and we’re already finished, we dismiss ourselves.
    if (documentId === iepIdParam && currentPercent === 100) {
      handleClose()
    }
  }, [documentId, iepIdParam, currentPercent, handleClose])

  const classes = useStyles(props)

  // Show nothing until progress > 0.0.
  if (currentPercent === 0.0) {
    return null
  }

  return (
    <section className={clsx(classes.root, className)}>
      <span className={classes.stageContainer}>
        <Emblem classes={{ root: classes.stageEmblem, icon: classes.stageIcon }} Icon={SummarySparkle} />

        <span className={classes.stageText}>Document analysis:</span>
      </span>

      <span className={classes.statusContainer}>
        {extractionCompleted ? (
          <span className={classes.terminalStatusContainer}>
            <DoneIcon className={clsx(classes.statusIcon, classes.readyIcon)} />
            Ready!
          </span>
        ) : status === PROGRESS_STATUS_FAILED ? (
          <span className={classes.terminalStatusContainer}>
            <ErrorIcon className={clsx(classes.statusIcon, classes.errorIcon)} /> {text}
          </span>
        ) : (
          <PercentRing className={classes.percentRing} percent={currentPercent} label={text} />
        )}
      </span>

      {extractionCompleted && (
        <BrandButton
          className={classes.reviewButton}
          variant="outlined"
          component={NavLink}
          to={iepPath(documentId, client)}
          onClick={handleTrackAndClose}
        >
          Review
        </BrandButton>
      )}

      <BrandIconButton className={classes.closeButton} onClick={handleClose}>
        <CloseIcon className={classes.closeIcon} />
      </BrandIconButton>
    </section>
  )
}

IepProgress.propTypes = {
  className: PropTypes.string,
  progress: PropTypes.object, // The object containing IEP progress information.
  onClose: PropTypes.func // Function to call when the close button is clicked.
}

export default IepProgress
